import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import React, { useMemo, useEffect, Suspense } from "react";
import { useSelector } from "react-redux";
import { CssBaseline, ThemeProvider, CircularProgress, Box, Typography, Button } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { themeSettings } from "./theme";
import RouteSkeleton from "./components/RouteSkeleton";
import PwaInstallPrompt from "./components/PwaInstallPrompt";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ErrorBoundary from "./app/ErrorBoundary";
import AppLayout from "./app/layouts/AppLayout";
import { RequireAuth, RedirectIfAuthed } from "./app/router/guards";
import { RouteSEO } from "./app/router/seo";
import { publicRoutes, publicOnlyRoutes, appRoutes } from "./app/router/routes";
import { ToastProvider } from "./app/providers/ToastProvider";

function NotFound() {
  const loc = useLocation()
  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh" textAlign="center" p={2}>
      <Typography variant="h1" fontWeight={800} color="primary" sx={{ fontSize: '5rem' }}>404</Typography>
      <Typography variant="h5" color="text.secondary" sx={{ mb: 3 }}>
        {loc.pathname === '/404' ? "This page doesn't exist." : "Let's get you back to learning."}
      </Typography>
      <Button variant="contained" size="large" href="/" sx={{ borderRadius: 2 }}>Go Home</Button>
    </Box>
  )
}

function AppContent() {
  const { user, loading } = useAuth();

  // Get theme mode from Redux store
  const mode = useSelector((state) => state.theme.mode);
  const customTheme = useSelector((state) => state.theme.customTheme);

  const theme = useMemo(() => createTheme(themeSettings(mode, customTheme)), [mode, customTheme]);

  // Dynamically update status bar / notch theme color based on Uben internal mode
  useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    const color = mode === 'dark' ? '#0f0f1c' : '#ffffff';
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', color);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'theme-color';
      meta.content = color;
      document.head.appendChild(meta);
    }
  }, [mode]);

  // Check for auth tokens in URL hash and redirect to email verification page
  useEffect(() => {
    const { pathname } = window.location;
    // The OAuth callback handles access_token itself — don't hijack it
    if (pathname === '/auth/callback') return;
    const hash = window.location.hash;
    if (hash.includes('access_token') && hash.includes('refresh_token')) {
      // Redirect to email verified page
      window.location.hash = '/email-verified';
    }
  }, []);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  // Determine background style
  const getBackgroundStyle = () => {
    const isPro = user?.profile?.plan === 'pro' || user?.profile?.is_admin === true;
    if (isPro && customTheme) {
      if (customTheme.startsWith('linear-gradient') || customTheme.startsWith('radial-gradient')) {
        return customTheme;
      } else if (customTheme.startsWith('rgba')) {
        return customTheme;
      }
    }
    return mode === 'dark'
      ? 'radial-gradient(circle farthest-corner at 10% 20%, rgba(97,186,255,1) 0%, rgba(166,239,253,1) 100%)'
      : 'linear-gradient(180deg, rgba(122,231,248,1) 6.5%, rgba(86,118,191,1) 100%)';
  };

  return (
    <div
      className="app"
      style={{
        background: getBackgroundStyle(),
        minHeight: '100vh'
      }}
    >
      <BrowserRouter>
          <RouteSEO />
          <ThemeProvider theme={theme}>
          <CssBaseline />
          <Suspense fallback={<RouteSkeleton />}>
            <Routes>
              <Route
                path="*"
                element={<NotFound />}
              />
              {publicRoutes.map((route) => (
                <Route key={route.path} path={route.path} element={route.element} />
              ))}
              {publicOnlyRoutes.map((route) => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={<RedirectIfAuthed>{route.element}</RedirectIfAuthed>}
                />
              ))}
              <Route element={<RequireAuth><AppLayout /></RequireAuth>}>
                {appRoutes.map((route) => (
                  <Route key={route.path} path={route.path} element={route.element} />
                ))}
              </Route>
            </Routes>
          </Suspense>
          <PwaInstallPrompt />
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
